// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import { extractUserFacingMessage } from '@/api/errorMessage';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types';
import { api } from '@api/client';
import { getAccessToken, clearLegacyLocalStorageToken, classifyApiError } from '@api/apiBase';
import { useChatStore } from './chatStore';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  authBootstrapDone: boolean;

  // Actions
  bootstrapAuth: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  loginWithAccessLink: (token: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<RegisterResult>;
  logout: () => void;
  fetchProfile: () => Promise<void>;
  clearError: () => void;
}

type RegisterResult = {
  email: string;
  message: string;
  email_verification_required: boolean;
};

type PersistedAuthSnapshot = {
  state?: {
    user?: User | null;
    isAuthenticated?: boolean;
  };
};

function readPersistedAuthSnapshot(): { user: User; isAuthenticated: true } | null {
  try {
    const raw = window.localStorage.getItem('auth-storage');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedAuthSnapshot;
    const user = parsed?.state?.user;
    const isAuthenticated = parsed?.state?.isAuthenticated === true;
    if (!isAuthenticated || !user) return null;
    return { user, isAuthenticated: true };
  } catch {
    return null;
  }
}

function reconcileChatStateForUser(userId: string | null) {
  useChatStore.getState().reconcileAuthUser(userId);
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      authBootstrapDone: false,

      bootstrapAuth: async () => {
        if (get().authBootstrapDone) return;

        // One-time cleanup of legacy localStorage token (ADR-025)
        clearLegacyLocalStorageToken();

        const isLocalhost =
          typeof window !== 'undefined' &&
          (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
        const e2eBypassEnabled =
          isLocalhost &&
          import.meta.env.VITE_ENABLE_CODEX_E2E_AUTH_BYPASS === '1' &&
          window.localStorage.getItem('__codex_e2e_auth_bypass') === '1';
        if (e2eBypassEnabled) {
          const inMemoryUser = get().isAuthenticated && get().user ? get().user : null;
          const persistedSnapshot = inMemoryUser ? null : readPersistedAuthSnapshot();
          const bypassUser = inMemoryUser ?? persistedSnapshot?.user ?? null;
          if (bypassUser) {
            reconcileChatStateForUser(bypassUser.id);
            set({
              user: bypassUser,
              isAuthenticated: true,
              isLoading: false,
              error: null,
              authBootstrapDone: true,
            });
            return;
          }
        }

        // ADR-025: use the unified token reader so persisted refreshes and
        // warm in-memory sessions share one auth bootstrap path.
        if (!getAccessToken()) {
          reconcileChatStateForUser(null);
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            authBootstrapDone: true,
          });
          return;
        }

        set({ isLoading: true, error: null });
        try {
          const { data } = await api.getProfile();
          reconcileChatStateForUser(data.id);
          set({
            user: data,
            isAuthenticated: true,
            isLoading: false,
            authBootstrapDone: true,
          });
        } catch (error) {
          // P4-2（BUG-15）：仅 401 清登录态；5xx/网络错误（含 apiBase 静默
          // 重试耗尽后）保留持久化会话不强登出——服务恢复后后续请求自愈。
          if (classifyApiError(error).kind === 'unauthorized') {
            api.logout();
            reconcileChatStateForUser(null);
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              authBootstrapDone: true,
            });
            return;
          }
          set({ isLoading: false, authBootstrapDone: true });
        }
      },

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const data = await api.login(email, password);
          reconcileChatStateForUser(data.user.id);
          set({
            user: data.user,
            isAuthenticated: true,
            isLoading: false,
            authBootstrapDone: true,
          });
        } catch (error: unknown) {
          const axiosErr = error as { response?: { data?: { detail?: string } } };
          const detail = axiosErr?.response?.data?.detail;
          set({
            error: typeof detail === 'string' && detail.trim() ? detail : extractUserFacingMessage(error),
            isLoading: false,
            isAuthenticated: false,
            authBootstrapDone: true,
          });
          throw error;
        }
      },

      // 访问链接直达登录（比赛评委场景）：/login?tk= 兑换，后端返回与
      // login 同构的 user/access_token；失败文案走后端 detail（如"已过期"）。
      loginWithAccessLink: async (token) => {
        set({ isLoading: true, error: null });
        try {
          const data = await api.loginWithAccessLink(token);
          reconcileChatStateForUser(data.user.id);
          set({
            user: data.user,
            isAuthenticated: true,
            isLoading: false,
            authBootstrapDone: true,
          });
        } catch (error: unknown) {
          const axiosErr = error as { response?: { data?: { detail?: string } } };
          const detail = axiosErr?.response?.data?.detail;
          set({
            error: typeof detail === 'string' && detail.trim() ? detail : extractUserFacingMessage(error),
            isLoading: false,
            isAuthenticated: false,
            authBootstrapDone: true,
          });
          throw error;
        }
      },

      register: async (email, password, name) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.register(email, password, name);
          const result = response.data as RegisterResult;
          set({ isLoading: false, authBootstrapDone: true });
          return result;
        } catch (error: unknown) {
          const axiosErr = error as { response?: { data?: { detail?: string } } };
          const detail = axiosErr?.response?.data?.detail;
          set({
            error: typeof detail === 'string' && detail.trim() ? detail : extractUserFacingMessage(error),
            isLoading: false,
            authBootstrapDone: true,
          });
          throw error;
        }
      },

      logout: () => {
        api.logout();
        reconcileChatStateForUser(null);
        // CI-H7: remove the persisted chat slice synchronously so a later
        // reopen (or another user logging in) can never rehydrate it. The
        // store reset above only writes back asynchronously.
        window.localStorage.removeItem('chat-storage');
        set({
          user: null,
          isAuthenticated: false,
          error: null,
          authBootstrapDone: true,
        });
      },

      fetchProfile: async () => {
        set({ isLoading: true, error: null });
        try {
          const { data } = await api.getProfile();
          reconcileChatStateForUser(data.id);
          set({
            user: data,
            isAuthenticated: true,
            isLoading: false,
            authBootstrapDone: true,
          });
        } catch {
          reconcileChatStateForUser(null);
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            authBootstrapDone: true,
          });
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
);
